import React, { FC } from 'react';
import { useHttpQuery } from '@/api';
import { API_URLS } from '../../consts';

interface ProgressionData {
  xp: number;
  totalXPEarned: number;
  gamesPlayed: number;
  wins: number;
  levelName: string;
  levelEmoji: string;
  currentLevelMinXP: number;
  nextLevelMinXP: number; // -1 if max level
}

interface ProgressionBarProps {
  userID: string;
}

export const progressPercent = (data: ProgressionData) => {
  if (data.nextLevelMinXP === -1) return 100;
  const range = data.nextLevelMinXP - data.currentLevelMinXP;
  if (range <= 0) return 100;
  const pct = Math.round(((data.xp - data.currentLevelMinXP) / range) * 100);
  return Math.max(0, Math.min(100, pct));
};

export const ProgressionBar: FC<ProgressionBarProps> = ({ userID }) => {
  const { data, isLoading } = useHttpQuery<ProgressionData>(
    ['userProgression', userID].join('-'),
    API_URLS.USER_PROGRESSION,
    'GET'
  );

  if (isLoading || !data) {
    return (
      <section className="bh-panel bh-progress" aria-busy={isLoading}>
        <p className="bh-small bh-muted" role="status">
          {isLoading
            ? 'Loading your progress…'
            : 'Play a game to start levelling up.'}
        </p>
      </section>
    );
  }

  const isMaxLevel = data.nextLevelMinXP === -1;
  const pct = progressPercent(data);
  const next = nextLevelName(data.levelName);

  return (
    <section className="bh-panel bh-progress" aria-labelledby="progress-title">
      <div className="bh-progress__head">
        <h2 id="progress-title" className="bh-h3">
          <span aria-hidden="true">{data.levelEmoji} </span>
          {data.levelName}
        </h2>
        <span className="bh-small bh-muted">
          {isMaxLevel
            ? `${data.xp} XP · Max level`
            : `${data.xp} / ${data.nextLevelMinXP} XP`}
        </span>
      </div>

      <div
        className="bh-progress__track"
        role="progressbar"
        aria-label={`Progress to ${isMaxLevel ? 'max level' : next}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div className="bh-progress__bar" style={{ width: `${pct}%` }} />
      </div>

      <ul className="bh-stats">
        <li className="bh-tag">
          <span aria-hidden="true">🃏</span> {data.gamesPlayed} games
        </li>
        <li className="bh-tag">
          <span aria-hidden="true">🏆</span> {data.wins} wins
        </li>
        {!isMaxLevel && (
          <li className="bh-tag">
            {data.nextLevelMinXP - data.xp} XP to {next}
          </li>
        )}
      </ul>
    </section>
  );
};

const LEVEL_ORDER = [
  'Beginner',
  'Rookie',
  'Intermediate',
  'Advanced',
  'Pro',
  'Expert',
  'King 👑',
];

export function nextLevelName(current: string): string {
  const idx = LEVEL_ORDER.findIndex(l => l.startsWith(current));
  if (idx === -1 || idx >= LEVEL_ORDER.length - 1) return 'max';
  return LEVEL_ORDER[idx + 1];
}
